"use client";

import React from "react";
import SmartCursorContext from "./context";
import type {
  SmartCursorContextProps,
  SmartCursorProviderProps,
} from "./types";
import { RenderCursor } from "../parts";
import { useViewport } from "@/components/providers";

const ENABLED_FLAG = false;

function SmartCursorProvider({ children }: SmartCursorProviderProps) {
  const [render, setRender] = React.useState(false);
  const [position, setPosition] = React.useState<
    SmartCursorContextProps["position"]
  >({ x: 0, y: 0 });
  const [cursorType, setCursorType] =
    React.useState<SmartCursorContextProps["cursorType"]>("default");
  const [cursorComponent, setCursorComponent] =
    React.useState<SmartCursorContextProps["cursorComponent"]>(null);
  const [hoverTarget, setHoverTarget] =
    React.useState<SmartCursorContextProps["hoverTarget"]>(null);
  const runOnce = React.useRef(false);
  const { isMobileMode } = useViewport();

  const resetCursor = React.useCallback(() => {
    setCursorType("default");
    setCursorComponent(null);
    setHoverTarget(null);
  }, []);

  const context = React.useMemo(
    () => ({
      position,
      cursorType,
      setCursorType,
      cursorComponent,
      setCursorComponent,
      hoverTarget,
      setHoverTarget,
      resetCursor,
    }),
    [position, cursorType, cursorComponent, resetCursor, hoverTarget],
  );

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      const x = e.clientX;
      const y = e.clientY;

      setPosition({ x, y });

      if (!hoverTarget) {
        runOnce.current = true;
        setRender(true);
        return;
      }

      const bounds = hoverTarget.getBoundingClientRect();
      const isInBounds =
        x >= bounds.left &&
        x <= bounds.right &&
        y >= bounds.top &&
        y <= bounds.bottom;

      // TODO: dont reset if we are already in default mode
      if (!isInBounds) {
        resetCursor();
      }

      if (!runOnce.current) {
        runOnce.current = true;
        setRender(true);
        return;
      }
    },
    [hoverTarget, resetCursor],
  );

  React.useEffect(() => {
    if (!ENABLED_FLAG || isMobileMode) {
      return;
    }

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isMobileMode, handleMouseMove]);

  // Conditionally Remove cursor based on flag
  React.useEffect(() => {
    if (isMobileMode) {
      return;
    }

    if (ENABLED_FLAG) {
      document.body.classList.add("smart-cursor-enabled");
    } else {
      document.body.classList.remove("smart-cursor-enabled");
    }
  }, [isMobileMode]);

  if (isMobileMode || !ENABLED_FLAG) {
    return children;
  }

  return (
    <SmartCursorContext.Provider value={context}>
      <RenderCursor render={render} {...context} />
      {children}
    </SmartCursorContext.Provider>
  );
}

export default React.memo(SmartCursorProvider);
